import { useEffect, useRef, useState, useCallback } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useAuth } from '@/hooks/use-auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const USE_MOCK_AP_INVOICES = import.meta.env.DEV && import.meta.env.VITE_MOCK_AP_INVOICES === '1'

/** Payload shape for all task:* events (matches backend build_notification_payload). */
export interface TaskEvent {
  uuid: string
  name: string
  status: string
  progress: number | null
  entity_type: string | null
  entity_id: number | null
  entity_label: string | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (data: any) => void

export function useSocket() {
  const { isAuthenticated, getAccessToken } = useAuth()
  const socketRef = useRef<Socket | null>(null)
  const connectionEpochRef = useRef(0)
  const retiringRef = useRef(false)
  const [isConnected, setIsConnected] = useState(false)

  // Track all active subscriptions so we can replay them when socket (re)connects.
  // This solves the React effect ordering issue: child effects run before parent
  // effects, so subscribers call on() before the socket exists.
  const listenersRef = useRef<Map<string, Set<Handler>>>(new Map())

  useEffect(() => {
    if (USE_MOCK_AP_INVOICES) return
    if (!isAuthenticated) return

    const socket = io(`${API_BASE_URL}/dashboard`, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      withCredentials: true,
      auth: async (cb) => {
        const token = await getAccessToken()
        cb({ token })
      },
    })

    socketRef.current = socket
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined

    // Attach any subscriptions that were registered before the socket existed
    for (const [event, handlers] of listenersRef.current) {
      for (const handler of handlers) {
        socket.on(event, handler)
      }
    }

    socket.on('connect', () => {
      retiringRef.current = false
      connectionEpochRef.current += 1
      setIsConnected(true)
    })
    socket.on('runtime:reconnect', (_data: unknown, acknowledge: (data: unknown) => void) => {
      retiringRef.current = true
      connectionEpochRef.current += 1
      acknowledge({ reconnect: true })
    })
    socket.on('runtime:resume', () => {
      retiringRef.current = false
    })
    socket.on('disconnect', (reason) => {
      connectionEpochRef.current += 1
      setIsConnected(false)
      if (reason === 'io server disconnect') {
        reconnectTimer = setTimeout(() => socket.connect(), 1000)
      }
    })
    socket.on('connect_error', (error: Error & { data?: { retry?: boolean } }) => {
      // A retiring target may still be selected by an affinity cookie. Keep
      // retrying without replaying an agent request or bypassing authentication.
      if (error.data?.retry) {
        reconnectTimer = setTimeout(() => socket.connect(), 1000)
      }
    })

    return () => {
      connectionEpochRef.current += 1
      clearTimeout(reconnectTimer)
      socket.disconnect()
      socketRef.current = null
      setIsConnected(false)
    }
  }, [isAuthenticated, getAccessToken])

  const on = useCallback((event: string, handler: Handler) => {
    // Track the subscription
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set())
    }
    listenersRef.current.get(event)!.add(handler)

    // If socket already exists, attach immediately
    socketRef.current?.on(event, handler)

    return () => {
      listenersRef.current.get(event)?.delete(handler)
      socketRef.current?.off(event, handler)
    }
  }, [])

  const emit = useCallback((event: string, payload?: unknown) => {
    socketRef.current?.emit(event, payload)
  }, [])

  // Emits with a freshly-acquired MSAL token merged into the payload as
  // `auth_token`. Use this for any event the backend needs to authorize on
  // its own — the token captured at connect time goes stale after ~1 hour.
  const emitWithAuth = useCallback(
    async (event: string, payload?: Record<string, unknown>) => {
      const socket = socketRef.current
      const socketId = socket?.id
      const connectionEpoch = connectionEpochRef.current
      if (
        !socket ||
        !socket.connected ||
        !socketId ||
        (retiringRef.current && event === 'agent:send')
      ) {
        throw new Error('Socket is not connected.')
      }
      const token = await getAccessToken()
      if (
        socketRef.current !== socket ||
        !socket.connected ||
        socket.id !== socketId ||
        connectionEpochRef.current !== connectionEpoch
      ) {
        throw new Error('Socket connection changed while authorizing the request.')
      }
      socket.emit(event, { ...(payload ?? {}), auth_token: token })
    },
    [getAccessToken],
  )

  return { isConnected, on, emit, emitWithAuth }
}
